import type {ContractChild, ContractPosition} from "./contractCoverageData";

export function formatCompactCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            notation: 'compact',
            maximumFractionDigits: Math.abs(value) >= 1_000_000 ? 2 : 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatNumber(value: number): string {
    return value.toLocaleString('en-US');
}

export function formatPercent(value: number): string {
    return `${value.toLocaleString('en-US', {maximumFractionDigits: 0})}%`;
}

export function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
}

// ---------------------------------------------------------------------------
// Contract positions
// ---------------------------------------------------------------------------

export const POSITION_LABEL: Record<ContractPosition, string> = {
    contract_required: 'Contract required',
    contract_recommended: 'Contract recommended',
    above_contracted_value: 'Above contracted value',
    below_expected_utilization: 'Below expected utilization',
    within_expected_utilization: 'Within expected utilization',
    no_contract_required: 'No contract required',
};

/** Sort / priority order — most actionable first. */
export const POSITION_ORDER: Record<ContractPosition, number> = {
    above_contracted_value: 0,
    contract_required: 1,
    contract_recommended: 2,
    below_expected_utilization: 3,
    within_expected_utilization: 4,
    no_contract_required: 5,
};

/** Background / text colours for a position pill (Mithra palette). */
export function positionColors(position: ContractPosition): {bg: string; fg: string} {
    switch (position) {
        case 'above_contracted_value':
            return {bg: 'rgba(194, 65, 12, 0.14)', fg: '#c2410c'};
        case 'contract_required':
            return {bg: 'rgba(255, 52, 53, 0.12)', fg: '#d32f2f'};
        case 'contract_recommended':
            return {bg: 'rgba(71, 143, 250, 0.14)', fg: '#2f6fd0'};
        case 'below_expected_utilization':
            return {bg: 'rgba(250, 169, 21, 0.16)', fg: '#cc880c'};
        case 'within_expected_utilization':
            return {bg: 'rgba(8, 165, 122, 0.14)', fg: '#08A57A'};
        case 'no_contract_required':
            return {bg: 'rgba(68, 71, 78, 0.08)', fg: '#44474E'};
    }
}

/** Recommended action for a contract position. */
export function actionForPosition(position: ContractPosition): string {
    switch (position) {
        case 'above_contracted_value':
            return 'Spend exceeds the contracted value — negotiate and expand scope.';
        case 'contract_required':
            return 'Put a contract in place — mandatory for this spend.';
        case 'contract_recommended':
            return 'Set up a contract — commercially advisable.';
        case 'below_expected_utilization':
            return 'Contract under-used versus expected — re-evaluate ahead of expiration.';
        case 'within_expected_utilization':
            return 'Coverage within the expected range — monitor.';
        case 'no_contract_required':
            return 'No contract required for this supplier.';
    }
}

/** Accent colour for a utilisation bar. */
export function utilizationColor(pct: number): string {
    if (pct > 100) return '#c2410c'; // above contracted value
    if (pct >= 80) return '#08A57A'; // within expected
    return '#cc880c';                // below expected
}

// ---------------------------------------------------------------------------
// Table filters (driven by the KPI cards)
// ---------------------------------------------------------------------------

export type CoverageFilterKey = 'required' | 'recommended' | 'above' | 'saving' | 'avoidance';

/** Does a child match the active filter? */
export function childMatchesFilter(ch: ContractChild, key: CoverageFilterKey): boolean {
    switch (key) {
        case 'required':
            return ch.position === 'contract_required';
        case 'recommended':
            return ch.position === 'contract_recommended';
        case 'above':
            return ch.position === 'above_contracted_value';
        case 'saving':
            return ch.costSavingEur > 0;
        case 'avoidance':
            return ch.costAvoidanceEur > 0;
    }
}

export const FILTER_LABEL: Record<CoverageFilterKey, string> = {
    required: 'Required contracts',
    recommended: 'Recommended contracts',
    above: 'Above contracted value',
    saving: 'Cost saving opportunity',
    avoidance: 'Cost avoidance',
};
